import React, { useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import { CheckCircle, Visibility, Block, Warning, Close } from '@mui/icons-material';
import { FileWithPreview } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';
import styles from './SourceNodeFileUpload.module.scss';

interface FileUploadBadgeProps {
  file: FileWithPreview;
  index: number;
  onClick: () => void;
  onRemove?: () => void;
  onHover?: (isHovered: boolean) => void;
}

export function FileUploadBadge({ file, index, onClick, onRemove, onHover }: FileUploadBadgeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isUploading = file.status === 'uploading';
  const isUploaded = file.status === 'uploaded';
  const isError = file.status === 'error';
  const isPending = file.status === 'pending' || !file.status;
  const isBlocked = file.isBlocked === true;
  const isOverLimit = file.isOverLimit === true;
  const isRestricted = isBlocked || isOverLimit;

  const uploadProgress = file.uploadProgress ?? 0;
  const backendProgress = file.backendUploadProgress ?? 0;
  const totalProgress = Math.round(uploadProgress * 0.4 + backendProgress * 0.6);

  const handleMouseEnter = () => {
    setIsHovered(true);
    onHover?.(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    onHover?.(false);
  };

  return (
    <Tooltip
      title={
        isRestricted && file.exclusionReason ? `${file.name} — ${file.exclusionReason}` : file.name
      }
      placement="top"
      arrow
      enterDelay={300}
    >
      <div
        className={`${styles.fileBadge} ${isUploaded ? styles.fileBadgeSuccess : ''} ${
          isError ? styles.fileBadgeError : ''
        } ${isUploading ? styles.fileBadgeUploading : ''} ${
          isRestricted ? styles.fileBadgeBlocked : ''
        } ${isPending && !isRestricted && isHovered ? styles.fileBadgeHovered : ''}`}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-file-badge="true"
      >
        {(isPending || isError || isRestricted) && isHovered && onRemove && (
          <div
            className={styles.removeBadge}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            data-file-badge="remove"
          >
            <Close style={{ fontSize: 'var(--font-size-sm)' }} />
          </div>
        )}

        {isUploading ? (
          <>
            <CircularProgress
              size={28}
              variant="determinate"
              value={totalProgress}
              className={styles.progressCircle}
              thickness={3}
            />

            <span className={styles.badgeNumber}>{index + 1}</span>
          </>
        ) : isUploaded ? (
          <>
            <CheckCircle className={styles.checkIcon} />
            <span className={styles.badgeNumber}>{index + 1}</span>
          </>
        ) : isRestricted ? (
          <>
            {isBlocked ? (
              <Block className={styles.restrictedIcon} />
            ) : (
              <Warning className={styles.restrictedIcon} />
            )}
            <span className={styles.badgeNumber}>{index + 1}</span>
          </>
        ) : isPending && isHovered ? (
          <>
            <Visibility className={styles.previewIcon} />
            <span className={styles.badgeNumber}>{index + 1}</span>
          </>
        ) : isError && isHovered ? (
          <>
            <Visibility className={styles.previewIcon} />
            <span className={styles.badgeNumber}>{index + 1}</span>
          </>
        ) : (
          <span className={styles.badgeNumber}>{index + 1}</span>
        )}
      </div>
    </Tooltip>
  );
}
